import { setFilters } from '@/app/common/reducers/filterSlice';
import type { AdvancedFiltersState } from '@/app/common/components/filters/filterTypes';
import { convertToQueryString } from '@/utils/queryString';
import {
  Button,
  Dropdown,
  Form,
  OverlayTrigger,
  Popover
} from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { useState, type Dispatch, type FormEvent, type SetStateAction } from 'react';

type HostItem = { host: string; i: number };
type PosFilterMode = 'all' | 'yours' | 'competitors';

type GapCustomPosFiltersProps = {
  submittedHosts: HostItem[];
};

type FiltersRootState = {
  filters: AdvancedFiltersState;
};

const GapCustomPosFilters = ({ submittedHosts }: GapCustomPosFiltersProps) => {
  const [, setShowFilterMenu] = useState(false);
  return (
    <OverlayTrigger
      trigger="click"
      rootClose
      placement="top"
      overlay={
        <Popover
          id="custom_filter_menu"
          className="react-select__menu"
          style={{ maxWidth: '100%', minWidth: 200 }}
        >
          <style>
            {`
          #custom_filter_menu .popover-arrow{ display:none }
          `}
          </style>
          <Popover.Body className="p-0">
            <div>
              <FilterItem
                submittedHosts={submittedHosts}
                setRootShow={setShowFilterMenu}
                title="همه دامنه ها"
                mode="all"
              />
              <FilterItem
                submittedHosts={submittedHosts}
                setRootShow={setShowFilterMenu}
                title="دامنه شما"
                mode="yours"
              />
              <FilterItem
                submittedHosts={submittedHosts}
                setRootShow={setShowFilterMenu}
                title="دامنه رقبا"
                mode="competitors"
              />
            </div>
          </Popover.Body>
        </Popover>
      }
    >
      <Button
        size="lg"
        variant="secondary"
        className="m-0 fs-0 h-100 cursor-pointer d-flex align-items-center"
      >
        رتبه میانگین
      </Button>
    </OverlayTrigger>
  );
};

export default GapCustomPosFilters;

type FilterItemProps = {
  mode: PosFilterMode;
  title: string;
  submittedHosts: HostItem[];
  setRootShow: Dispatch<SetStateAction<boolean>>;
};

const FilterItem = ({
  mode,
  title,
  submittedHosts,
  setRootShow
}: FilterItemProps) => {
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const dispatch = useDispatch();
  const currentFilters = useSelector(
    (state: FiltersRootState) => state.filters
  );

  const [, setSearchParams] = useSearchParams();

  const [customStart, setCustomStart] = useState<string | number>(1);
  const [customLimit, setCustomLimit] = useState('');

  const handleFilterClick = (limit: number | string) => {
    let filters: AdvancedFiltersState = {};

    switch (mode) {
      case 'all':
        filters = submittedHosts.reduce<AdvancedFiltersState>((acc, { host }) => {
          acc[host] = {
            title: host,
            operator: 'and',
            filterModes: [
              { mode: 'greaterthanorequals', value: `${customStart}`, key: 1 },
              { mode: 'lessthanorequals', value: limit, key: 2 }
            ]
          };
          return acc;
        }, {});
        break;

      case 'yours':
        if (submittedHosts.length > 0) {
          const myDomain = submittedHosts[0].host;
          filters[myDomain] = {
            title: myDomain,
            operator: 'and',
            filterModes: [
              { mode: 'greaterthanorequals', value: `${customStart}`, key: 1 },
              { mode: 'lessthanorequals', value: limit, key: 2 }
            ]
          };
        }
        break;

      case 'competitors':
        filters = submittedHosts.slice(1).reduce<AdvancedFiltersState>(
          (acc, { host }) => {
            acc[host] = {
              title: host,
              operator: 'and',
              filterModes: [
                {
                  mode: 'greaterthanorequals',
                  value: `${customStart}`,
                  key: 1
                },
                { mode: 'lessthanorequals', value: limit, key: 2 }
              ]
            };
            return acc;
          },
          {}
        );
        break;

      default:
        break;
    }

    dispatch(setFilters({ ...currentFilters, ...filters }));
    setSearchParams(convertToQueryString({ ...currentFilters, ...filters }));
    setShowFilterMenu(false);
    setRootShow(false);
  };

  const handleCustomSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (customLimit) {
      handleFilterClick(customLimit);
    }
  };

  return (
    <OverlayTrigger
      onToggle={() => setShowFilterMenu(false)}
      show={showFilterMenu}
      trigger="click"
      rootClose
      placement="left-start"
      overlay={
        <Popover
          id="custom_filter_menu"
          className="react-select__menu"
          style={{ maxWidth: '100%', minWidth: 200 }}
        >
          <style>
            {`
          #custom_filter_menu .popover-arrow{ display:none }
          `}
          </style>
          <Popover.Body className="p-2">
            <Dropdown.Item
              className="link-600 border-bottom fs--1 justify-content-between d-flex align-items-center gap-1 p-1"
              onClick={() => handleFilterClick(10)}
            >
              بالای ۱۰
            </Dropdown.Item>
            <Dropdown.Item
              className="link-600 border-bottom fs--1 justify-content-between d-flex align-items-center gap-1 p-1"
              onClick={() => handleFilterClick(20)}
            >
              بالای ۲۰
            </Dropdown.Item>
            <Dropdown.Item
              className="link-600 border-bottom fs--1 justify-content-between d-flex align-items-center gap-1 p-1"
              onClick={() => handleFilterClick(50)}
            >
              بالای ۵۰
            </Dropdown.Item>
            <div onClick={e => e.stopPropagation()} className="p-2 mt-2">
              <Form onSubmit={handleCustomSubmit}>
                <Form.Label> بازه دلخواه </Form.Label>
                <div className="input-group position-relative input-group-sm">
                  <Form.Control
                    size="sm"
                    type="number"
                    placeholder="از"
                    style={{ width: 50 }}
                    value={customStart}
                    onChange={e => setCustomStart(e.target.value)}
                  />
                  <Form.Control
                    size="sm"
                    type="number"
                    style={{ width: 50 }}
                    placeholder="تا"
                    value={customLimit}
                    onChange={e => setCustomLimit(e.target.value)}
                  />
                </div>
                <Button
                  type="submit"
                  size="sm"
                  variant="falcon-primary"
                  className="mt-2 w-100"
                >
                  اعمال
                </Button>
              </Form>
            </div>
          </Popover.Body>
        </Popover>
      }
    >
      <Dropdown.Item
        onClick={() => setShowFilterMenu(s => !s)}
        className="link-600 p-2 border-bottom fs--1 justify-content-between d-flex align-items-center gap-1 p-1"
      >
        {title}
      </Dropdown.Item>
    </OverlayTrigger>
  );
};
